/**
 * Region listener middleware.
 *
 * On a region change (setRegion) it:
 *   1. Syncs the selected region into the URL (?region=) so it survives a
 *      refresh and is shareable — no browser storage.
 *   2. Re-dispatches whatever query is currently active (the detail pages'
 *      last entity id, and each search slice's last input) so the newly
 *      selected region's API is called immediately — without any page useEffect.
 *
 * Slices reset their currentId / lastInput to null when cleared (on unmount),
 * so a region change only re-runs queries that are actually on screen.
 */
import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from './store';
import { setRegion } from './slices/env/envSlice';
import { fetchPaymentDetail } from './slices/paymentDetail/paymentDetailSlice';
import { fetchArtifactDetail } from './slices/artifactDetail/artifactDetailSlice';
import { fetchExecutionDetail } from './slices/executionDetail/executionDetailSlice';
import { searchPayments } from './slices/searchPayments/searchPaymentsSlice';
import { searchArtifacts } from './slices/searchArtifacts/searchArtifactsSlice';
import { searchExecutions } from './slices/searchExecutions/searchExecutionsSlice';

// Created UNTYPED on purpose: typing it <RootState, AppDispatch> would make the store's dispatch
// type depend on this middleware and that back on AppDispatch — a type cycle. Cast inside the effect.
export const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
  actionCreator: setRegion,
  effect: (action, listenerApi) => {
    // 1. Persist the region in the URL (survives refresh / shareable).
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('region', action.payload);
      window.history.replaceState(null, '', url.toString());
    }

    // 2. Re-run whatever query is active so it hits the new region's API.
    const state = listenerApi.getState() as RootState;
    const dispatch = listenerApi.dispatch as AppDispatch;
    if (state.paymentDetail.currentId) dispatch(fetchPaymentDetail(state.paymentDetail.currentId));
    if (state.artifactDetail.currentId) dispatch(fetchArtifactDetail(state.artifactDetail.currentId));
    if (state.executionDetail.currentId) dispatch(fetchExecutionDetail(state.executionDetail.currentId));
    // Skipped for a global search: the bulk page is single-region only, and the
    // artifact / execution pages re-drive their payment search off the region
    // their own lookup landed in.
    if (!state.env.global && state.searchPayments.lastInput) {
      dispatch(searchPayments({ input: state.searchPayments.lastInput }));
    }
    if (state.searchArtifacts.lastInput) dispatch(searchArtifacts(state.searchArtifacts.lastInput));
    if (state.searchExecutions.lastInput) dispatch(searchExecutions(state.searchExecutions.lastInput));
  },
});
